/**
 * Marketing → Google Business.
 *
 * Phase 1: connecting a location, and seeing what is connected. Overview,
 * Content and Reviews follow; the tabs are declared here so the shape of the
 * section is visible from the first release rather than appearing later.
 *
 * WHY CONNECTING IS NOT ON THIS PAGE. Google Business talks DIRECTLY to Google
 * (Zernio is social publishing only and is not involved). OAuth alone does not
 * finish the job either — one authorisation can cover many listings and someone
 * has to say which belong to this account — but all of that is an administrative
 * act done once, so it lives in Admin → Integrations → Marketing. This page is
 * for the people reading reviews every day and is deliberately read-only about
 * the connection.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, ExternalLink, MapPin } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import { GoogleOverviewTab } from './GoogleOverviewTab';
import { GoogleReviewsTab } from './GoogleReviewsTab';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import {
  REVIEW_FILTER_CONFIGS,
  REVIEW_FILTER_OPTIONS,
  useReviewFilters,
} from './reviewFilters';
import {
  getGoogleBusinessStatus,
  type GoogleBusinessStatus,
} from '@/services/googleBusiness.service';

/** Where Google sends the browser back to. Absolute, and our own origin. */
/**
 * What is connected, and where to change it.
 *
 * Read-only on purpose. Connecting a Google account is an administrative act
 * done once, and it lives in Admin → Integrations → Marketing. This screen is
 * for the people reading reviews every day, who should not be shown setup they
 * will never touch.
 */
function LocationsTab({ status }: { status: GoogleBusinessStatus }) {
  if (!status.googleConfigured) {
    return (
      <EmptyState
        icon={<Building2 className="h-10 w-10 text-slate-300" />}
        title="Google Business is not set up on this environment"
        description="Sign-in credentials for Google have not been configured yet, so no account can be connected. This is a deployment setting rather than something to retry here."
      />
    );
  }

  if (status.needsReconnect) {
    return (
      <EmptyState
        icon={<Building2 className="h-10 w-10 text-slate-300" />}
        title="The Google connection needs renewing"
        description="Google is no longer accepting this connection, so reviews cannot be read or answered. An administrator can reconnect it under Admin → Integrations → Marketing."
      />
    );
  }

  if (!status.connected) {
    return (
      <EmptyState
        icon={<Building2 className="h-10 w-10 text-slate-300" />}
        title="No Google Business locations connected"
        description="An administrator can connect the Google account under Admin → Integrations → Marketing. Once a location is linked it appears here, with its reviews and performance."
      />
    );
  }

  return (
    <div className="space-y-3">
      {status.locations.map((a) => (
        <div
          key={a.id}
          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4"
        >
          <div className="flex min-w-0 items-center gap-3">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">{a.name}</p>
              <p className="truncate text-xs text-slate-500">Connected</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="success">Active</Badge>
            {/* Profile administration stays on Google — it already does it, and
                better than a rebuild would. */}
            <a href="https://business.google.com/" target="_blank" rel="noreferrer">
              <Button variant="secondary" size="sm" rightIcon={<ExternalLink className="h-3.5 w-3.5" />}>
                Manage on Google
              </Button>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GoogleBusinessPage() {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<GoogleBusinessStatus | null>(null);
  const [loading, setLoading] = useState(true);
  // Kept apart from `status`: a failed load leaves status null, and without this
  // the page rendered a spinner forever behind a toast the user had dismissed.
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * Which connected location Overview and Reviews are showing.
   *
   * Held here rather than in each tab so switching on one carries to the other:
   * having Overview on Century City while Reviews quietly showed West Hollywood
   * would be a worse bug than the one this fixes.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);


  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setStatus(await getGoogleBusinessStatus());
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Google Business status could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Default to the first location, and fall back to it if the selected one is
  // no longer connected — otherwise disconnecting leaves both tabs querying an
  // account that does not exist.
  useEffect(() => {
    const ids = status?.locations.map((a) => a.id) ?? [];
    if (ids.length === 0) {
      setSelectedId(null);
    } else if (!selectedId || !ids.includes(selectedId)) {
      setSelectedId(ids[0]);
    }
  }, [status, selectedId]);



  /**
   * Rendered above Overview and Reviews, and ONLY when more than one location is
   * connected — a select with a single option is a statement dressed as a
   * choice.
   */
  const locationSelect =
    (status?.locations.length ?? 0) > 1 ? (
      <div className="mb-4 max-w-sm">
        <Select
          label="Location"
          options={(status?.locations ?? []).map((a) => ({ value: a.id, label: a.name }))}
          value={selectedId}
          onChange={(v) => setSelectedId(v)}
        />
      </div>
    ) : null;

  // Which of the three views the URL is asking for. The section is one
  // component across three routes, so the connection status and the chosen
  // location survive moving between them — splitting it into three pages would
  // refetch both on every click.
  // Owned by the page rather than the tab: the filter rail is a page-level panel
  // beside the main navigation (the Patients position), so the rail and the
  // results are siblings sharing one piece of state.
  const reviewFilters = useReviewFilters();

  const view = pathname.endsWith('/overview')
    ? 'overview'
    : pathname.endsWith('/reviews')
      ? 'reviews'
      : 'locations';

  const notConnected = (description: string) => (
    <EmptyState
      icon={<Building2 className="h-10 w-10 text-slate-300" />}
      title="Connect a location first"
      description={description}
    />
  );

  const body =
    view === 'locations' ? (
      loading ? (
        <SpinnerOverlay label="Loading Google Business…" />
      ) : loadError || !status ? (
        /* An error has to be a state on the page, not only a toast — a toast is
           gone in five seconds and the user is left with nothing to act on. */
        <EmptyState
          icon={<Building2 className="h-10 w-10 text-slate-300" />}
          title="Google Business could not be loaded"
          description={loadError ?? 'Something went wrong loading this section.'}
          action={{ label: 'Try again', variant: 'secondary', onClick: () => void refresh() }}
        />
      ) : (
        <LocationsTab status={status} />
      )
    ) : loading ? (
      <SpinnerOverlay label="Loading Google Business…" />
    ) : !selectedId ? (
      notConnected(
        view === 'overview'
          ? 'Once a Google Business location is connected, how its listing is performing appears here.'
          : 'Once a Google Business location is connected, its reviews appear here and can be answered.',
      )
    ) : (
      <>
        {locationSelect}
        {view === 'overview' ? (
          <GoogleOverviewTab key={selectedId} locationId={selectedId} />
        ) : (
          <GoogleReviewsTab key={selectedId} locationId={selectedId} filters={reviewFilters} />
        )}
      </>
    );

  const page = (
    <div className="p-6">
      <PageHeader
        title="Google Business"
        subtitle="Connect this account's Google Business Profile, then manage its listing, posts and reviews."
      />
      <div className="mt-4">{body}</div>
    </div>
  );

  /**
   * Only Reviews gets the filter rail, and only once a location is chosen.
   *
   * ListPageLayout is the same shell Patients uses, which is what puts the panel
   * flush against the main navigation rather than inside the content card. The
   * other two views have nothing to filter, and showing an empty rail beside
   * them would be a control that does nothing.
   */
  if (view === 'reviews' && selectedId) {
    return (
      <ListPageLayout
        filterSidebar={
          <FilterSidebar
            filters={REVIEW_FILTER_CONFIGS}
            values={reviewFilters.filterState.values}
            options={REVIEW_FILTER_OPTIONS}
            onChange={reviewFilters.filterState.setFilter}
            onClear={reviewFilters.filterState.clearFilter}
            showAIAssistant={false}
            storageKey="google-reviews"
          />
        }
      >
        {/* ListPageLayout's content column is `overflow-hidden flex flex-col`, so
            whatever it wraps has to own its scrolling — Patients' DataTable does
            this itself. Without this container the review list simply runs past
            the bottom of the column and is clipped, taking the pagination
            controls with it and leaving no way to reach page 2. */}
        <div className="min-h-0 flex-1 overflow-y-auto">{page}</div>
      </ListPageLayout>
    );
  }

  return page;
}
